import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
} from "@mui/material";
import { ChatSessionDto } from "@/apis/types/SuperChatLogTypes";

interface StatisticsTabProps {
  session: ChatSessionDto;
}

/**
 * 통계 정보 탭 컴포넌트
 *
 * @description 세션의 통계 정보와 성능 지표를 표시하는 탭
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
export const StatisticsTab: React.FC<StatisticsTabProps> = ({ session }) => {
  const completionRate = parseFloat(session.completionRate || "0");
  const avgProcessingTime =
    session.totalProcessingTime && session.totalMessages
      ? Math.round(session.totalProcessingTime / session.totalMessages)
      : 0;

  const formatCost = (cost: string | undefined) => {
    if (!cost) return "$0.00";
    // 비정상적인 긴 문자열 처리
    if (cost.length > 10) {
      return "$0.00";
    }
    return `$${cost}`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* 메시지 통계 카드 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                메시지 통계
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    총 메시지 수:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" color="primary">
                    {session.totalMessages || 0}개
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    사용자 메시지:
                  </Typography>
                  <Typography variant="body2">
                    {session.userMessages || 0}개
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    AI 메시지:
                  </Typography>
                  <Typography variant="body2">
                    {session.botMessages || 0}개
                  </Typography>
                </Box>

                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      완료율:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {completionRate.toFixed(1)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={completionRate}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>

                {session.satisfactionScore && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      만족도 점수:
                    </Typography>
                    <Chip
                      label={`${session.satisfactionScore}/5`}
                      size="small"
                      color={
                        session.satisfactionScore >= 4
                          ? "success"
                          : session.satisfactionScore >= 3
                          ? "warning"
                          : "error"
                      }
                    />
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 성능 정보 카드 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                성능 정보
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    총 처리 시간:
                  </Typography>
                  <Typography variant="body2">
                    {session.totalProcessingTime || 0}ms
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    평균 응답 시간:
                  </Typography>
                  <Typography variant="body2">{avgProcessingTime}ms</Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    총 토큰 수:
                  </Typography>
                  <Typography variant="body2">
                    {session.totalTokens || 0}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    총 비용:
                  </Typography>
                  <Typography
                    variant="body2"
                    color="success.main"
                    fontWeight="medium"
                  >
                    {formatCost(session.totalCost)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 세션 상태 정보 카드 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                세션 상태
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    해결 상태:
                  </Typography>
                  <Chip
                    label={session.resolutionStatus || "OPEN"}
                    size="small"
                    color={
                      session.resolutionStatus === "RESOLVED"
                        ? "success"
                        : "warning"
                    }
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    후속 조치 필요:
                  </Typography>
                  <Chip
                    label={session.followUpRequired ? "필요" : "불필요"}
                    size="small"
                    color={session.followUpRequired ? "warning" : "success"}
                  />
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    긴급도 점수:
                  </Typography>
                  <Typography variant="body2">
                    {session.urgencyScore || 0}/100
                  </Typography>
                </Box>

                {session.outcomeType && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      결과 유형:
                    </Typography>
                    <Chip
                      label={session.outcomeType}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 추가 메타데이터 카드 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                추가 정보
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {session.campaignId && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      캠페인 ID:
                    </Typography>
                    <Typography variant="body2">
                      {session.campaignId}
                    </Typography>
                  </Box>
                )}

                {session.referrerUrl && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      유입 URL:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 200,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {session.referrerUrl}
                    </Typography>
                  </Box>
                )}

                {session.assignedAdminId && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      담당 관리자:
                    </Typography>
                    <Typography variant="body2">
                      {session.assignedAdminId}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

